/**
 * AnalyticsSettingsSection - Manages analytics cutoff date for an organization
 *
 * For OrgAdmin users only. Allows setting a "clean slate" date for analytics.
 * Reports before the cutoff are excluded from response time and open reports calculations.
 * On mobile, this section is read-only with a prompt to use web for editing.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { isWeb as checkIsWeb } from '../../util/platform';
import { View, StyleSheet, Pressable, ActivityIndicator, Modal } from 'react-native'
import { Feather } from '@expo/vector-icons';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';
import { DateTime } from 'luxon';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { formatLongDate } from '../../util/format';
import { useTranslation } from '../../i18n';

/**
 * @param {string} organization - Organization name
 * @param {boolean} readOnly - If true, show read-only view (for mobile)
 */
const AnalyticsSettingsSection = ({ organization, readOnly = false }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [cutoffDate, setCutoffDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState('');
  const [datePickerError, setDatePickerError] = useState(null);

  const isWeb = checkIsWeb();
  const canEdit = !readOnly && isWeb;

  const fetchCutoffDate = useCallback(async () => {
    if (!organization) return;

    setLoading(true);
    setError(null);

    try {
      const response = await Util.get('Resty', '/items/organization/analytics-cutoff', {
        queryParams: { organization },
      });

      if (response?.data?.analytics_cutoff_date) {
        setCutoffDate(response.data.analytics_cutoff_date);
      } else {
        setCutoffDate(null);
      }
    } catch (err) {
      console.error('Error fetching analytics cutoff:', err);
      setError(t('analyticsBaseline.loadFailed'));
      setCutoffDate(null);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchCutoffDate();
  }, [fetchCutoffDate]);

  const handleOpenDatePicker = () => {
    // Pre-fill with today's date
    setDatePickerValue(DateTime.now().toISODate());
    setDatePickerError(null);
    setShowDatePicker(true);
  };

  const handleCloseDatePicker = () => {
    setShowDatePicker(false);
    setDatePickerValue('');
    setDatePickerError(null);
  };

  const handleSetDate = async () => {
    // Parse date from native date input (ISO format: YYYY-MM-DD)
    const parsed = DateTime.fromISO(datePickerValue.trim(), { zone: 'local' });

    if (!parsed.isValid) {
      setDatePickerError(t('analyticsBaseline.invalidDate'));
      return;
    }

    // Check if the date (not time) is in the future
    const today = DateTime.now().startOf('day');
    if (parsed.startOf('day') > today) {
      setDatePickerError(t('analyticsBaseline.futureDate'));
      return;
    }

    // Use start of day as cutoff - reports from this date onwards will be included
    const timestamp = parsed.startOf('day').toMillis();

    setSaving(true);
    setError(null);
    setDatePickerError(null);

    try {
      const response = await Util.patch('Resty', '/items/organization/analytics-cutoff', {
        body: {
          organization,
          analytics_cutoff_date: timestamp,
        },
      });

      if (response?.error) {
        setError(response.error);
        return;
      }

      setCutoffDate(response?.data?.analytics_cutoff_date || null);
      handleCloseDatePicker();
    } catch (err) {
      console.error('Error updating analytics cutoff:', err);
      // Extract error message from various response structures
      const errorMessage = err?.response?.data?.error || err?.message || t('analyticsBaseline.saveFailed');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClearCutoff = async () => {
    setShowConfirmClear(false);
    setSaving(true);
    setError(null);

    try {
      const response = await Util.patch('Resty', '/items/organization/analytics-cutoff', {
        body: {
          organization,
          analytics_cutoff_date: null,
        },
      });

      if (response?.error) {
        setError(response.error);
        return;
      }

      setCutoffDate(null);
    } catch (err) {
      console.error('Error clearing analytics cutoff:', err);
      setError(err.message || t('analyticsBaseline.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    return formatLongDate(timestamp);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('analyticsBaseline.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  if (error && !cutoffDate) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Feather name="alert-circle" size={20} color="#B91C1C" />
          <CustomText style={styles.errorText} size={14} color="#B91C1C">
            {error}
          </CustomText>
          <Pressable onPress={fetchCutoffDate} style={styles.retryButton}>
            <CustomText size={13} color="#1D4ED8">
              {t('analyticsBaseline.retry')}
            </CustomText>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CustomText
        style={styles.heading}
        font="semiBold"
        size={18}
        color={AppStyles.colorSet.textPrimary}
      >
        {t('analyticsBaseline.title')}
      </CustomText>
      <CustomText
        style={styles.explanationText}
        font="regular"
        size={14}
        color={AppStyles.colorSet.textSecondary}
      >
        {t('analyticsBaseline.description')}
      </CustomText>

      {error && (
        <View style={styles.inlineError}>
          <Feather name="alert-circle" size={14} color="#B91C1C" />
          <CustomText style={styles.inlineErrorText} size={13} color="#B91C1C">
            {error}
          </CustomText>
        </View>
      )}

      {/* Current status */}
      <View style={styles.statusCard}>
        {cutoffDate ? (
          <View style={styles.statusRow}>
            <View style={styles.statusIcon}>
              <Feather name="calendar" size={20} color={AppStyles.colorSet.brandPrimary} />
            </View>
            <View style={styles.statusContent}>
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                {t('analyticsBaseline.baselineSet')}
              </CustomText>
              <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                {t('analyticsBaseline.metricsFrom', { date: formatDate(cutoffDate) })}
              </CustomText>
            </View>
          </View>
        ) : (
          <View style={styles.statusRow}>
            <View style={[styles.statusIcon, styles.statusIconMuted]}>
              <Feather name="bar-chart-2" size={20} color={AppStyles.colorSet.textSecondary} />
            </View>
            <View style={styles.statusContent}>
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                {t('analyticsBaseline.noBaseline')}
              </CustomText>
              <CustomText size={13} color={AppStyles.colorSet.textSecondary}>
                {t('analyticsBaseline.noBaselineHint')}
              </CustomText>
            </View>
          </View>
        )}
      </View>

      {/* Action buttons */}
      {canEdit && (
        <View style={styles.actionsContainer}>
          {!showDatePicker ? (
            <View style={cutoffDate ? styles.actionButtonsRow : undefined}>
              <Pressable
                style={({ pressed, hovered, focused }) => [
                  cutoffDate ? styles.secondaryButton : styles.primaryButton,
                  cutoffDate && styles.flexButton,
                  { cursor: 'pointer' },
                  !saving && (hovered || focused) && styles.actionButtonHovered,
                  pressed && styles.buttonPressed,
                  saving && styles.buttonDisabled,
                ]}
                onPress={handleOpenDatePicker}
                disabled={saving}
              >
                {saving && !showConfirmClear ? (
                  <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
                ) : (
                  <>
                    <Feather name="calendar" size={16} color={AppStyles.colorSet.textPrimary} />
                    <CustomText style={styles.buttonText} font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                      {cutoffDate ? t('analyticsBaseline.changeDate') : t('analyticsBaseline.setBaselineDate')}
                    </CustomText>
                  </>
                )}
              </Pressable>
              {cutoffDate && (
                <Pressable
                  style={({ pressed, hovered, focused }) => [
                    styles.dangerButton,
                    styles.flexButton,
                    { cursor: 'pointer' },
                    !saving && (hovered || focused) && styles.dangerButtonHovered,
                    pressed && styles.buttonPressed,
                    saving && styles.buttonDisabled,
                  ]}
                  onPress={() => setShowConfirmClear(true)}
                  disabled={saving}
                >
                  {saving && showConfirmClear ? (
                    <ActivityIndicator size="small" color={AppStyles.colorSet.error} />
                  ) : (
                    <>
                      <Feather name="x-circle" size={16} color={AppStyles.colorSet.error} />
                      <CustomText style={styles.dangerButtonText} font="medium" size={14} color={AppStyles.colorSet.error}>
                        {t('analyticsBaseline.clearBaseline')}
                      </CustomText>
                    </>
                  )}
                </Pressable>
              )}
            </View>
          ) : (
            <View style={styles.datePickerContainer}>
              <CustomText style={styles.datePickerLabel} size={13} color={AppStyles.colorSet.textSecondary}>
                {t('analyticsBaseline.selectDate')}
              </CustomText>
              <View style={styles.datePickerRow}>
                <input
                  type="date"
                  style={{
                    flex: 1,
                    backgroundColor: '#FFFFFF',
                    border: `1px solid ${datePickerError ? AppStyles.colorSet.error : '#E5E5E5'}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 14,
                    color: AppStyles.colorSet.textPrimary,
                    fontFamily: 'inherit',
                  }}
                  value={datePickerValue}
                  onChange={(e) => {
                    setDatePickerValue(e.target.value);
                    setDatePickerError(null);
                  }}
                  max={DateTime.now().toISODate()}
                  disabled={saving}
                />
                <Pressable
                  style={({ pressed, hovered, focused }) => [
                    styles.setDateButton,
                    { cursor: 'pointer' },
                    datePickerValue.trim() && (hovered || focused) && styles.actionButtonHovered,
                    !datePickerValue.trim() && styles.buttonDisabled,
                    pressed && styles.buttonPressed,
                  ]}
                  onPress={handleSetDate}
                  disabled={saving || !datePickerValue.trim()}
                >
                  {saving ? (
                    <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
                  ) : (
                    <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                      {t('analyticsBaseline.set')}
                    </CustomText>
                  )}
                </Pressable>
                <Pressable
                  style={({ pressed, hovered, focused }) => [
                    styles.cancelButton,
                    { cursor: 'pointer' },
                    (hovered || focused) && styles.cancelButtonHovered,
                    pressed && styles.buttonPressed,
                  ]}
                  onPress={handleCloseDatePicker}
                  disabled={saving}
                >
                  <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('common:actions.cancel')}
                  </CustomText>
                </Pressable>
              </View>
              {datePickerError && (
                <View style={styles.datePickerErrorContainer}>
                  <Feather name="alert-circle" size={14} color="#B91C1C" />
                  <CustomText style={styles.datePickerErrorText} size={13} color="#B91C1C">
                    {datePickerError}
                  </CustomText>
                </View>
              )}
            </View>
          )}
        </View>
      )}

      {/* Mobile read-only notice */}
      {readOnly && !isWeb && (
        <View style={styles.webOnlyNotice}>
          <Feather name="monitor" size={16} color={AppStyles.colorSet.textSecondary} />
          <CustomText style={styles.webOnlyText} size={13} color={AppStyles.colorSet.textSecondary}>
            {t('analyticsBaseline.editOnWeb')}
          </CustomText>
        </View>
      )}

      {/* Confirmation Modal for clearing */}
      <Modal
        visible={showConfirmClear}
        transparent
        animationType="fade"
        onRequestClose={() => setShowConfirmClear(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
              {t('analyticsBaseline.clearConfirmTitle')}
            </CustomText>
            <CustomText style={styles.modalText} size={14} color={AppStyles.colorSet.textSecondary}>
              {t('analyticsBaseline.clearConfirmMessage')}
            </CustomText>
            <View style={styles.modalButtons}>
              <Pressable
                style={({ hovered, focused, pressed }) => [
                  styles.modalCancelButton,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.cancelButtonHovered,
                  pressed && { opacity: 0.7 },
                ]}
                onPress={() => setShowConfirmClear(false)}
              >
                <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                  {t('common:actions.cancel')}
                </CustomText>
              </Pressable>
              <Pressable
                style={({ hovered, focused }) => [
                  styles.modalConfirmButton,
                  styles.modalConfirmButtonDanger,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.actionButtonHovered,
                ]}
                onPress={handleClearCutoff}
              >
                <CustomText font="medium" size={14} color="#FFFFFF">
                  {t('analyticsBaseline.clear')}
                </CustomText>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: hp(2),
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    padding: 8,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  inlineError: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    padding: 10,
    borderRadius: 8,
    marginBottom: hp(1),
  },
  inlineErrorText: {
    marginLeft: 8,
    flex: 1,
  },
  statusCard: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: hp(1.5),
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#E0F2FE',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  statusIconMuted: {
    backgroundColor: '#F3F4F6',
  },
  statusContent: {
    flex: 1,
  },
  actionsContainer: {
    marginTop: hp(0.5),
  },
  primaryButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
  },
  secondaryButton: {
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    gap: 8,
  },
  dangerButton: {
    backgroundColor: '#FEF2F2',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
    gap: 8,
  },
  // Web hover/focus feedback shared across the settings action buttons
  actionButtonHovered: {
    opacity: 0.94,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 2,
  },
  dangerButtonHovered: {
    borderColor: '#FCA5A5',
    backgroundColor: '#FEE2E2',
  },
  cancelButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 8,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  flexButton: {
    flex: 1,
  },
  buttonText: {
    marginLeft: 4,
  },
  dangerButtonText: {
    marginLeft: 4,
  },
  buttonPressed: {
    opacity: 0.8,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  datePickerContainer: {
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 8,
  },
  datePickerLabel: {
    marginBottom: 8,
  },
  datePickerRow: {
    flexDirection: 'row',
    gap: 8,
  },
  setDateButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  datePickerErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  datePickerErrorText: {
    marginLeft: 6,
  },
  webOnlyNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginTop: hp(1),
  },
  webOnlyText: {
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalText: {
    marginTop: 12,
    marginBottom: 20,
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  modalCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  modalConfirmButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  modalConfirmButtonDanger: {
    backgroundColor: AppStyles.colorSet.error,
  },
});

export default AnalyticsSettingsSection;
